// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {connectionToNodes} from '@wandb/weave/core';
import React, {useCallback, useMemo} from 'react';

import {
  useCreateArtifactTagAssignmentsMutation,
  useDeleteArtifactTagAssignmentsMutation,
  useEntityTagsQuery,
} from '../../generated/graphql';
import {ENTITY_TAGS_QUERY} from '../../state/graphql/entityTagsQuery';
import * as GQLTypes from '../../types/graphql';
import {getErrMsgFromError, propagateErrorsContext} from '../../util/errors';
import {useIsProjectReadOnly} from '../Registries/common/hooks';
import {
  AddTagAssignmentButton,
  createAttributesString,
  mapGQLTags,
  matchTag,
  ModifiedTag,
  parseTag,
  TagAssignment,
  TagAttributes,
} from '../TagAssignment';
import {useArtifactMembershipTags} from './hooks';

type TagListProps = {
  showButton: boolean;
  showColor: boolean;
  tagSize: 'small' | 'medium' | 'large';
  wrap?: boolean;
  showLoader?: boolean;
};

type ArtifactMembershipTagListProps = {
  artifactCollectionID: string;
  artifactVersion: number;
};

export const ArtifactMembershipTagList: React.FC<
  ArtifactMembershipTagListProps & TagListProps
> = props => {
  const {entityName, projectName, artifactId, tags, loading, refetch, error} =
    useArtifactMembershipTags(
      props.artifactCollectionID,
      props.artifactVersion
    );

  const entityTagsQuery = useEntityTagsQuery({
    // entityTags are only needed while rendering the list of tags
    // when a user tries to add a new tag. So the query result is not
    // needed if we're rendering the list without the 'add' button
    skip: !entityName || !props.showButton,
    variables: {
      entityName: entityName ?? '',
    },
  });
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(entityName ?? '', projectName ?? '');

  const entity = entityTagsQuery.data?.entity;
  const allTags = useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    return mapGQLTags(rawTags);
  }, [entity]);

  if (loading || tags == null || error || isProjectReadOnlyLoading) {
    return <></>;
  }

  // We don't want to display the error message in case of the
  // entity query failing as they only list out existing tags on the collection
  if (entityTagsQuery.error) {
    const errMsg = getErrMsgFromError(entityTagsQuery.error);
    console.error(`Error retrieving entity tags; Msg: ${errMsg}`);
  }

  return (
    <ArtifactMembershipTagListInner
      {...props}
      refresh={refetch}
      entityName={entityName ?? ''}
      artifactVersionId={artifactId ?? ''}
      currentTags={tags}
      allTags={allTags}
      showButton={props.showButton && !readOnly}
    />
  );
};

type ArtifactMembershipTagListInnerProps = {
  refresh: () => void;
  entityName: string;
  artifactVersionId: string;
  currentTags: ModifiedTag[];
  allTags: ModifiedTag[];
};

export const ArtifactMembershipTagListInner: React.FC<
  ArtifactMembershipTagListInnerProps & TagListProps
> = props => {
  const [addTagAssignment] = useCreateArtifactTagAssignmentsMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ENTITY_TAGS_QUERY,
        variables: {entityName: props.entityName},
      },
    ],
  });

  const createTagAssignment = useCallback(
    async (tagInput: GQLTypes.Tag) => {
      try {
        const tagStr = tagInput.name;
        if (props.currentTags.some((t: ModifiedTag) => matchTag(t, tagStr))) {
          return Promise.resolve();
        }

        // Parse input string into tag and category
        const [tagCategoryName, tagName] = parseTag(tagStr);

        // Stringify Attributes
        const tagAttributes: TagAttributes = {
          color: tagInput.colorIndex,
          icon: tagInput.icon,
        };
        const attributesStr = createAttributesString(tagAttributes);
        await addTagAssignment({
          variables: {
            artifactID: props.artifactVersionId,
            tags: [
              {
                tagCategoryName,
                tagName,
                attributes: attributesStr,
              },
            ],
          },
        });
        props.refresh();
      } catch (error) {
        console.error(error);
        toast(
          'Something went wrong when trying to create this tag. Please check for special characters.'
        );
      }
    },
    [addTagAssignment, props]
  );

  const [removeTagAssignment] = useDeleteArtifactTagAssignmentsMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ENTITY_TAGS_QUERY,
        variables: {entityName: props.entityName},
      },
    ],
  });

  const deleteTagAssignment = useCallback(
    async (tag: ModifiedTag) => {
      try {
        await removeTagAssignment({
          variables: {
            artifactID: props.artifactVersionId,
            tags: [
              {
                tagName: tag.name,
                tagCategoryName: tag.tagCategoryName,
              },
            ],
          },
        });
        props.refresh();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to delete this tag.');
      }
      return;
    },
    [removeTagAssignment, props]
  );

  return (
    <Tailwind>
      <div className={`flex ${props.wrap ? 'flex-wrap gap-y-5' : ''}`}>
        {props.currentTags.map(tag => {
          return (
            <TagAssignment
              key={tag.tagCategoryName + tag.name}
              tag={tag}
              showColor={props.showColor}
              size={props.tagSize}
              canDelete={props.showButton}
              onDelete={() => deleteTagAssignment(tag)}
            />
          );
        })}
        {props.showButton && (
          // Workaround: Prevent clipping of left edge of AddTagAssignmentButton if there are no assigned tags.
          //  See `marginLeft` value here: https://github.com/wandb/weave/blob/52ca63da5cc4c07ab4a5ca8e41203b10a5e061cf/weave-js/src/common/components/Tags.tsx#L70
          <div style={{marginLeft: props.currentTags ? '2px' : undefined}}>
            <AddTagAssignmentButton
              assignedTags={props.currentTags}
              availableTags={props.allTags}
              onCreate={createTagAssignment}
            />
          </div>
        )}
      </div>
    </Tailwind>
  );
};
